import { NzModalService } from 'ng-zorro-antd/modal';

import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Session } from '@services/session';
import { CustomValidators } from '@services/custom-validators';
import { PlatformFeatureFlagService } from '@services/feature-flags/platform';
import { PersonaSurveyModalService, userRoles, companyRoles, industries } from './persona-survey-modal.service';

import { moment } from '@lib/moment';
import { inDemoMode, inReadOnlyMode, inSupportMode } from '@lib/remote-login';

enum STEPS {
  USER_ROLE,
  FOR_HIRE,
  COMPANY_TYPE,
}

const FOR_HIRE_TRUCKING = 'For-Hire Trucking';
const DAYS: number = 4;

@Component({
  providers: [CustomValidators],
  selector: 'persona-survey-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./persona-survey-modal.component.scss'],
  templateUrl: './persona-survey-modal.component.html',
})
export class PersonaSurveyModalComponent implements OnInit {
  public userCompanyRoleForm: FormGroup;
  public forHireForm: FormGroup;
  public industryForm: FormGroup;
  public frieghtForm: FormGroup;

  public userRoles: string[] = userRoles;
  public companyRoles: string[] = companyRoles;
  public industries: string[] = industries;

  public stepsList: number[] = [STEPS.USER_ROLE, STEPS.FOR_HIRE, STEPS.COMPANY_TYPE];
  public STEPS: typeof STEPS = STEPS;
  public OTHER: string = $localize`Other`;
  public NEXT: string = $localize`Next`;
  public SUBMIT: string = $localize`Submit`;
  public currentStepIndex: number = 0;
  public totalSteps = this.stepsList.length;

  public companyFormError: string = null;
  public selectedCompanies: string[] = [];

  @ViewChild('contentTemplate', { static: true }) private contentTemplate: TemplateRef<any>;
  @ViewChild('footerTemplate', { static: true }) private footerTemplate: TemplateRef<any>;
  @ViewChild('closeIconTemplate', { static: true }) private closeIcon: TemplateRef<any>;

  constructor(
    public session: Session,
    private modal: NzModalService,
    private service: PersonaSurveyModalService,
    private fb: FormBuilder,
    private customValidators: CustomValidators,
    private platformFeatureFlagService: PlatformFeatureFlagService,
  ) { }

  public ngOnInit(): void {
    const userRole = this.session.getUserCompanyRole();
    const industry = this.session.getIndustry();
    const freights = this.session.getFreight();

    this.initSteps(userRole, industry, freights);

    if (!inSupportMode()
      && !inDemoMode()
      && !this.isUserNew(DAYS)
      && !this.session.isDriver()
      && !this.didUserExitOutToday()
      && !this.service.closedByUser
      && this.stepsList[this.currentStepIndex] > -1
      && !inReadOnlyMode()
      && this.platformFeatureFlagService.isPersonaSurveyEnabled()
    ) {
      const attempts: number = this.session.getPersonaSurveyAttempts();
      if (attempts > 1) this.createModal(false);
      else this.createModal(true);
    } else return;

    this.userCompanyRoleForm = this.fb.group({
      userCompanyRole: [userRole && !this.userRoles[userRole] ? this.OTHER : userRole, [Validators.required]],
      userCompanyRoleInput: [this.userRoles[userRole] ? '' : userRole, [Validators.required, this.customValidators.isBlank()]],
    });

    let initForHire: string = null;
    if (industry && industry !== FOR_HIRE_TRUCKING) initForHire = 'false';
    else if (industry && industry === FOR_HIRE_TRUCKING) initForHire = 'true';
    this.forHireForm = this.fb.group({
      forHire: [initForHire , [Validators.required]],
    });

    this.industryForm = this.fb.group({
      industry: ['', Validators.required],
      industryInput: ['', [Validators.required, this.customValidators.isBlank()]],
    });

    this.frieghtForm = this.fb.group({
      freightInput: ['', [Validators.required, this.customValidators.isBlank()]],
    });
  }

  public validateUserCompanyRoleForm(): boolean {
    const userRole: string = this.userCompanyRoleForm.controls.userCompanyRole.value;
    (userRole === this.OTHER)
      ? this.userCompanyRoleForm.controls.userCompanyRoleInput
        .setValidators([Validators.required, this.customValidators.isBlank()])
      : this.userCompanyRoleForm.controls.userCompanyRoleInput.clearValidators();
    for (const i of Object.keys(this.userCompanyRoleForm.controls)) {
      this.userCompanyRoleForm.controls[i].markAsDirty();
      this.userCompanyRoleForm.controls[i].updateValueAndValidity();
    }
    return this.userCompanyRoleForm.valid;
  }

  public validateForHireForm(): boolean {
    for (const i of Object.keys(this.forHireForm.controls)) {
      this.forHireForm.controls[i].markAsDirty();
      this.forHireForm.controls[i].updateValueAndValidity();
    }
    return this.forHireForm.valid;
  }

  public validateIndustryForm(): boolean {
    const industry = this.industryForm.controls.industry.value;
    (industry === this.OTHER)
      ? this.industryForm.controls.industryInput.setValidators([Validators.required, this.customValidators.isBlank()])
      : this.industryForm.controls.industryInput.clearValidators();
    for (const i of Object.keys(this.industryForm.controls)) {
      this.industryForm.controls[i].markAsDirty();
      this.industryForm.controls[i].updateValueAndValidity();
    }
    return this.industryForm.valid;
  }

  public onCompanySelectionChange(selectedCompanies): void {
    this.selectedCompanies = selectedCompanies;
    this.resetCompanyFormError();
  }

  public validateFreightForm(): boolean {
    this.resetCompanyFormError();
    (this.selectedCompanies.indexOf(this.OTHER) > -1)
      ? this.frieghtForm.controls.freightInput.setValidators([Validators.required, this.customValidators.isBlank()])
      : this.frieghtForm.controls.freightInput.clearValidators();
    this.frieghtForm.controls.freightInput.markAsDirty();
    this.frieghtForm.controls.freightInput.updateValueAndValidity();
    return this.frieghtForm.valid;
  }

  public nextStep(): void {
    const currentStep: number = this.stepsList[this.currentStepIndex];
    const forHire: boolean = this.forHireForm.controls.forHire.value === 'true';
    switch (currentStep) {
    case STEPS.USER_ROLE:
      if (!this.validateUserCompanyRoleForm()) return;
      let userRole = this.userCompanyRoleForm.controls.userCompanyRole.value;
      if (userRole === this.OTHER) userRole = this.userCompanyRoleForm.controls.userCompanyRoleInput.value.trim();
      this.service.updateUsersCompanyRole(userRole).subscribe();
      break;
    case STEPS.FOR_HIRE:
      if (!this.validateForHireForm()) return;
      if (forHire) this.service.updateIndustry(FOR_HIRE_TRUCKING).subscribe();
      else this.service.updateIndustry('').subscribe();
      break;
    case STEPS.COMPANY_TYPE:
      if (!forHire) {
        if (!this.validateIndustryForm()) return;
        let industry = this.industryForm.controls.industry.value;
        if (industry === this.OTHER) industry = this.industryForm.controls.industryInput.value.trim();
        this.service.updateIndustry(industry).subscribe();
      } else {
        if (!this.validateFreightForm() || this.companyFormError) return;
        if (this.selectedCompanies.indexOf(this.OTHER) > -1) {
          this.selectedCompanies[this.selectedCompanies.indexOf(this.OTHER)] =
          this.frieghtForm.controls.freightInput.value.trim();
        }
        this.service.updateFreightCarriers(this.selectedCompanies).subscribe();
      }
      break;
    }
    if (this.currentStepIndex < this.totalSteps - 1) this.currentStepIndex++;
    else {
      this.service.closedByUser = true;
      this.modal.closeAll();
    }

    const companyName = this.session.getCompanyProperty('name');
    if (this.currentStepIndex === 1 && companyName.toLowerCase().indexOf('trucking') > -1) {
      this.forHireForm.controls.forHire.setValue('true');
    }
  }

  private resetCompanyFormError(): void {
    this.companyFormError = this.selectedCompanies.length > 0 ? null : $localize`Please select freight type`;
  }

  private isUserNew(day) {
    return moment().isBefore(moment(this.session.getUserCreationDate()).add(day, 'd'));
  }

  private didUserExitOutToday(): boolean {
    const surveyLastSubmitDate = this.session.getPersonaSurveyLastSubmitDate();
    return (surveyLastSubmitDate) ? moment(surveyLastSubmitDate).isSame(moment(), 'd') : false;
  }

  private initSteps(role, industry, freights) {
    if (this.session.isDriver()) this.currentStepIndex = -1;
    else if (this.session.isAdmin()) {
      if ((industry && industry !== FOR_HIRE_TRUCKING)
      || (freights && freights.length > 0)) this.totalSteps = 1;
      if (role && this.totalSteps === 1) this.currentStepIndex = -1;
      else {
        if (role) this.currentStepIndex++;
        if (role && industry === FOR_HIRE_TRUCKING) this.currentStepIndex++;
      }
    } else {
      if (role) this.currentStepIndex = -1;
      this.totalSteps = 1;
    }
  }

  private createModal(closeable: boolean): void {
    this.modal.create({
      nzTitle: $localize`Company Survey`,
      nzContent: this.contentTemplate,
      nzFooter: this.footerTemplate,
      nzMaskClosable: false,
      nzClosable: closeable,
      nzCloseIcon: this.closeIcon,
      nzOnCancel: () => {
        this.service.closedByUser = true;
        this.service.updateAttempts().subscribe();
      },
    });
  }

}
